import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { Citation, DocumentSummary, IngestionJob, KnowledgeStatus } from "../types";

type Banner = { kind: "ok" | "err" | "info"; text: string } | null;

const TERMINAL_JOB_STATES = new Set(["COMPLETE", "FAILED", "STOPPED"]);
const NEW_DOCUMENT_TEMPLATE = `---
title: New document
doc_id: DOC-000
category: support_article
version: 2026-09
---

# New document

Write the knowledge content here.
`;

/**
 * Knowledge base administration.
 *
 * Managed RAG is otherwise a black box: you cannot see what the model is grounded in,
 * and an edit is not retrievable until Bedrock has re-indexed it. This screen exposes
 * the corpus, makes re-indexing explicit and observable, and provides a retrieval probe
 * so a change can be verified without leaving the page.
 */
export function KnowledgeBase() {
  const [status, setStatus] = useState<KnowledgeStatus | null>(null);
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [dirty, setDirty] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [job, setJob] = useState<IngestionJob | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Citation[] | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [nextStatus, nextDocuments] = await Promise.all([
        api.knowledgeStatus(),
        api.listDocuments(),
      ]);
      setStatus(nextStatus);
      setDocuments(nextDocuments);
    } catch (error) {
      setBanner({ kind: "err", text: `Could not load the knowledge base: ${error}` });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Ingestion is asynchronous - poll until the job reaches a terminal state so the UI
  // never implies an edit is searchable before Bedrock has indexed it.
  useEffect(() => {
    if (!job || TERMINAL_JOB_STATES.has(job.status)) return;
    const timer = window.setTimeout(async () => {
      try {
        setJob(await api.syncStatus(job.job_id));
      } catch (error) {
        setBanner({ kind: "err", text: `Could not read the ingestion job: ${error}` });
        setJob(null);
      }
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [job]);

  const open = async (key: string) => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    try {
      const document = await api.readDocument(key);
      setSelected(key);
      setContent(document.content);
      setDirty(false);
      setBanner(null);
    } catch (error) {
      setBanner({ kind: "err", text: `Could not open ${key}: ${error}` });
    }
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.writeDocument(selected, content);
      setDirty(false);
      setBanner({
        kind: "ok",
        text:
          status?.backend === "bedrock"
            ? `Saved ${selected}. Run a re-index to make the change retrievable.`
            : `Saved ${selected}.`,
      });
      await refresh();
    } catch (error) {
      setBanner({ kind: "err", text: `Save failed: ${error}` });
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    const key = window.prompt("New document name (e.g. product-c-travel.md)");
    if (!key) return;
    setBusy(true);
    try {
      await api.writeDocument(key, NEW_DOCUMENT_TEMPLATE);
      await refresh();
      await open(key);
      setBanner({
        kind: "ok",
        text:
          status?.backend === "bedrock"
            ? `Created ${key}. Run a re-index to make it retrievable.`
            : `Created ${key}.`,
      });
    } catch (error) {
      setBanner({ kind: "err", text: `Could not create the document: ${error}` });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!selected || !window.confirm(`Delete ${selected}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.deleteDocument(selected);
      setSelected(null);
      setContent("");
      setDirty(false);
      await refresh();
      setBanner({ kind: "ok", text: "Document deleted." });
    } catch (error) {
      setBanner({ kind: "err", text: `Delete failed: ${error}` });
    } finally {
      setBusy(false);
    }
  };

  const reindex = async () => {
    setBusy(true);
    try {
      const started = await api.startSync();
      setJob(started);
      setBanner({ kind: "info", text: "Ingestion job started." });
    } catch (error) {
      setBanner({ kind: "err", text: `Could not start ingestion: ${error}` });
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async () => {
    if (query.trim().length < 2) return;
    setBusy(true);
    try {
      setResults(await api.search(query.trim()));
    } catch (error) {
      setBanner({ kind: "err", text: `Search failed: ${error}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kb">
      <section className="kb-list">
        <header>
          <span>{status ? `${status.document_count} documents` : "Corpus"}</span>
          <button className="kb-btn" onClick={() => void create()} disabled={busy}>
            New
          </button>
        </header>
        <ul>
          {documents.map((document) => (
            <li key={document.key}>
              <button
                className={selected === document.key ? "active" : ""}
                onClick={() => void open(document.key)}
              >
                {document.key}
                <span className="size">
                  {(document.size / 1024).toFixed(1)} kB, edited{" "}
                  {new Date(document.last_modified).toLocaleDateString()}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {documents.length === 0 && <p className="empty">No documents yet.</p>}
      </section>

      <section className="kb-main">
        <header>
          <span className="name">{selected ?? "No document open"}</span>
          {dirty && <span className="kb-hit">Unsaved</span>}
          <button
            className="kb-btn strong"
            onClick={() => void save()}
            disabled={!selected || !dirty || busy}
          >
            Save changes
          </button>
          <button
            className="kb-btn"
            onClick={() => void reindex()}
            disabled={busy}
            title={
              status?.backend === "bedrock"
                ? "Starts a Bedrock ingestion job"
                : "Local corpus is read directly, so this is a no-op"
            }
          >
            Re-index
          </button>
          <button
            className="kb-btn risk"
            onClick={() => void remove()}
            disabled={!selected || busy}
          >
            Delete
          </button>
        </header>

        {banner && <div className={`kb-banner ${banner.kind}`}>{banner.text}</div>}

        {job && (
          <div className="kb-banner info">
            Ingestion job {job.job_id} is <strong>{job.status}</strong>
            {!TERMINAL_JOB_STATES.has(job.status) && ", checking again shortly"}
          </div>
        )}

        {selected ? (
          <textarea
            value={content}
            spellCheck={false}
            onChange={(event) => {
              setContent(event.target.value);
              setDirty(true);
            }}
          />
        ) : (
          <p className="empty">
            Pick a document on the left to read or edit it. Everything here is what the
            Copilot quotes from when it answers a cover question.
          </p>
        )}

        <div className="kb-search">
          <div className="row">
            <input
              value={query}
              placeholder="Ask what an agent would ask, and see what comes back"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void runSearch()}
            />
            <button
              className="kb-btn"
              onClick={() => void runSearch()}
              disabled={busy || query.trim().length < 2}
            >
              Search
            </button>
          </div>
          {results && results.length === 0 && <p className="kb-hit">Nothing matched.</p>}
          {results?.map((citation) => (
            <div className="kb-hit" key={`${citation.source}-${citation.title}`}>
              <div className="title">{citation.title}</div>
              <p className="extract">{citation.snippet}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
